import { type RequestServiceOptions, utils } from '@atlaskit/util-service-support';

import { generateHash } from '../../../utils/caching/common/key-generator';
import { refreshOnAccess } from '../../../utils/caching/persist-refresh';

const RELATIVE_URL = '/gateway/api/xpsearch-aggregator/configuration/v1';
const RELATIVE_3P_URL = '/gateway/api/third-party-configuration/connected-data-sources';

const { refreshOnAccessDecorator: refreshOnAccessDecoratorSearch } =
	refreshOnAccess<TSearchConfigAPIResponse>({ name: 'search-config' });

export const SEARCH_3P_CONFIG: string = 'search-3p-config';
const { refreshOnAccessDecorator: refreshOnAccessDecorator3pSearch } =
	refreshOnAccess<TSearch3pConfigAPIResponse>({
		name: SEARCH_3P_CONFIG,
		expiration: 1000 * 60 * 5,
	});

const SITELESS_PRODUCTS = ['trello', 'bitbucket'];

export type TSearchConfigProps = {
	/**
	 * When passed this would be used as is to invoke the endpoint.
	 * This is useful for products like Trello where the API gateway is not on the host domain, but a completely different URL.
	 *
	 * When specified it takes precedence over `aggregatorHostName`.
	 */
	absoluteUrl?: string;

	/**
	 * If passed then only the resources which are tied to the given cloudId are returned.
	 */
	cloudId?: string;

	/**
	 * When passed this would be used to invoke the endpoint. Example: https://product-fabric.atlassian.net
	 * This is used ONLY if absoluteUrl is not specified.
	 */
	hostName?: string;

	/**
	 * Identifier of the user
	 */
	userId?: string | null;
};

export type TSearch3pConfigProps = {
	/**
	 * When passed this would be used as is to invoke the endpoint.
	 * This is useful for products like Trello where the API gateway is not on the host domain, but a completely different URL.
	 */
	absoluteUrl?: string;

	cloudId?: string;

	/**
	 * Identifier of the user
	 */
	userId?: string | null;
};

export type TResource = {
	baseUrl: string;
	id: string; // this is actually an ARI , example - ari:cloud:confluence::site/690973c4-d02a-470f-b795-0af9b28f75c1
	siteName: string;
};

export type TSiteMetadata = {
	isNounsAvailable: boolean;
	siteId: string;
	siteName: string;
};

export type T3pResource = {
	isUserOAuthed: boolean;
	outboundAuthUrl: string;
	type: string;
};

export type TSearchConfigAPIResponse = {
	intercomHmac: string;
	resources: TResource[];
	siteMetadata: TSiteMetadata[];
};

export type TSearch3pConfigAPIResponse = T3pResource[];

/**
 * Sample response:
 * {
    "resources": [
        {
            "id": "ari:cloud:avp::site/DUMMY-158c8204-ff3b-47c2-adbb-a0906ccc722b",
            "baseUrl": "https://product-fabric.atlassian.net",
            "siteName": "product-fabric"
        },
      ]
    }
 */

export const getSearchConfig = async ({
	absoluteUrl = '',
	hostName,
	cloudId,
	userId,
}: TSearchConfigProps): Promise<TSearchConfigAPIResponse> => {
	const url = absoluteUrl || `${hostName || ''}${RELATIVE_URL}`;

	const options: RequestServiceOptions = {
		requestInit: {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		},
	};

	const key = generateHash(`1p|${userId}|${cloudId}`);
	const supplier = () =>
		utils
			.requestService<TSearchConfigAPIResponse>({ url }, options)
			.then((data: TSearchConfigAPIResponse) => {
				const resources = data.resources.filter((resource) =>
					cloudId
						? resource.id.includes(cloudId) ||
							SITELESS_PRODUCTS.find((product) => resource.id.includes(product)) // Allow access to products that are not associated with a site
						: true,
				);

				return {
					resources: resources,
					intercomHmac: data.intercomHmac || '',
					siteMetadata: data.siteMetadata || [],
				};
			});

	return refreshOnAccessDecoratorSearch(key.toString(), supplier);
};

/**
 * Sample response:
 * [
      {
        "type": "ai-3p-connector:google-workspace",
        "location": "ari:third-party:google::site/DUMMY-0c418054-69d8-4a6c-bec6-8ef053070a36"
      }
    ]
 */

export const get3pSearchConfig = async ({
	absoluteUrl = '',
	cloudId,
	userId,
}: TSearch3pConfigProps): Promise<TSearch3pConfigAPIResponse> => {
	const baseUrl = absoluteUrl || RELATIVE_3P_URL;
	const url = `${baseUrl}/${cloudId}`;

	const options: RequestServiceOptions = {
		requestInit: {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		},
	};
	const key = generate3pHash(userId || '', cloudId || '');

	const supplier = () => utils.requestService<TSearch3pConfigAPIResponse>({ url }, options);

	return refreshOnAccessDecorator3pSearch(key.toString(), supplier, false);
};

export const generate3pHash = (userId: string, cloudId: string) =>
	generateHash(`3p|${userId}|${cloudId}`);
